import React, { useState, useRef, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import SidebarStudent from "./Utils/SidebarStudent";
import CreateGraduationdetails, { GraduationdetailsFormRef } from "./Resource/CreateGraduationdetails";
import CreateEducation10thDetails from "./Resource/CreateEducation10thDetails";
import CreateEducation12thDetails from "./Resource/CreateEducation12thDetails";
import CreateOlympiad from "./Resource/CreateOlympiad";

import "./Dashboard.css";
import "./EducationDetails.css";
import { toast } from "../utils/toast";
import { useStudentProgress } from "../context/StudentProgressContext";
import LogoutButton from "./LogoutButton";
import { useAdmissionDriveKind } from "../hooks/useAdmissionDriveKind";
import { LoginContext } from "../context/LoginContext";
import apiConfig from "../config/apiConfig";
import { apiFetch } from "../apis/apiFetch";
import { fetchForeignResource } from "../apis/resources";
import { validate } from "../utils/validator";
import {
  btechEducation10ApplicationRules,
  btechEducation12ApplicationRules,
  btechOlympiadApplicationRules,
} from "../validators/applicantValidator";
import { saveApplicationRecord } from "../services/applicationRecordService";
import { BTechApplicationFields } from "../config/btechApplicationFields";

export default function EducationDetails() {
  const navigate = useNavigate();
  const { refreshProgress } = useStudentProgress();
  const { user } = useContext(LoginContext);
  const { kind, loading: driveLoading } = useAdmissionDriveKind();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const graduationRef = useRef<GraduationdetailsFormRef>(null);

  const [btechDraft, setBtechDraft] = useState<Record<string, any>>({});
  const [btechFieldErrors, setBtechFieldErrors] = useState<Record<string, string>>({});
  const [btechDraftLoaded, setBtechDraftLoaded] = useState(false);

  const patchBtech = useCallback((patch: Record<string, any>) => {
    setBtechDraft((d) => ({ ...d, ...patch }));
  }, []);

  useEffect(() => {
    if (kind !== "btech" || !user?.userId || !user?.email_id) return;
    let cancelled = false;
    (async () => {
      try {
        const [appRes, users] = await Promise.all([
          apiFetch(
            `${apiConfig.getResourceUrl("applications")}?queryId=GET_APPLICATIONS_BY_USER_ID&args=user_id:${user.userId}`,
          ),
          fetchForeignResource("Users"),
        ]);
        if (!appRes.ok || cancelled) return;
        const apps = (await appRes.json()).resource || [];
        const activeDriveId = localStorage.getItem("activeDriveId");
        const row =
          apps.find((a: any) => a.user_id === user.userId && a.drive_id === activeDriveId) || apps[0];
        if (!row || cancelled) return;

        const match = users.find((u: any) => {
          const email = (u.email || u.email_id || u.user_email || "").toLowerCase().trim();
          return email === user.email_id!.toLowerCase().trim();
        });

        setBtechDraft({
          ...row,
          ...(match?.id ? { user_id: match.id } : {}),
          [BTechApplicationFields.edu10.level]: row[BTechApplicationFields.edu10.level] || "10",
          [BTechApplicationFields.edu10.specialization]:
            row[BTechApplicationFields.edu10.specialization] || "General",
          [BTechApplicationFields.edu12.level]: row[BTechApplicationFields.edu12.level] || "12",
        });
        setBtechDraftLoaded(true);
      } catch (e) {
        console.error(e);
        toast.error("Could not load application.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, user?.userId, user?.email_id]);

  const handleSaveAndNext = async () => {
    try {
      setIsSaving(true);

      if (kind === "btech") {
        const e10 = validate(btechDraft, btechEducation10ApplicationRules);
        const e12 = validate(btechDraft, btechEducation12ApplicationRules);
        const eOly = validate(btechDraft, btechOlympiadApplicationRules);
        const merged = { ...e10, ...e12, ...eOly };
        setBtechFieldErrors(merged);
        if (Object.keys(merged).length > 0) {
          toast.error("Please complete all required fields.");
          return;
        }

        const res = await saveApplicationRecord(btechDraft, { userIdForDms: user?.userId });
        if (res.errCode === -1) {
          toast.error(res.message || "Save failed.");
          return;
        }

        await refreshProgress();
        toast.success("Education details saved successfully!");
        setTimeout(() => navigate("/examdetails"), 1000);
        return;
      }

      if (graduationRef.current) {
        await graduationRef.current.save();
        const gErrors = graduationRef.current.getErrors();
        if (Object.keys(gErrors).length > 0) {
          toast.error("Please complete all required fields.");
          return;
        }
      }

      await refreshProgress();
      toast.success("Education details saved successfully!");
      setTimeout(() => navigate("/programmedetails"), 1000);
    } catch (error) {
      console.error("Error saving details:", error);
      toast.error("Error saving details. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (driveLoading) {
    return (
      <div className="dash-layout">
        <SidebarStudent
          sidebarCollapsed={sidebarCollapsed}
          toggleSidebar={() => setSidebarCollapsed((p) => !p)}
        />
        <div className={`dash-wrapper ${sidebarCollapsed ? "dash-wrapper-collapsed" : ""}`}>
          <header className="dash-header">
            <h1 className="dash-page-title">Education Details</h1>
            <div className="dash-user-profile">
              <div className="dash-profile-circle"><span>S</span></div>
            </div>
          </header>
          <div className="dash-content-body"><p style={{ padding: "1rem" }}>Loading…</p></div>
        </div>
      </div>
    );
  }

  const showBtech = kind === "btech" && btechDraftLoaded;

  return (
    <div className="dash-layout">
      <SidebarStudent
        sidebarCollapsed={sidebarCollapsed}
        toggleSidebar={() => setSidebarCollapsed((p) => !p)}
      />

      <div
        className={`dash-wrapper ${sidebarCollapsed ? "dash-wrapper-collapsed" : ""
          }`}
      >
        <header className="dash-header">
          <h1 className="dash-page-title">Education Details</h1>

          <div className="dash-user-profile">
            <div
              className="dash-profile-circle"
              onClick={() => setShowDropdown((p) => !p)}
            >
              <span>S</span>
            </div>

            {showDropdown && (
              <div className="dash-profile-dropdown">
                <LogoutButton />
              </div>
            )}
          </div>
        </header>

        <div className="dash-content-body">
          {kind === "btech" && !btechDraftLoaded ? (
            <p style={{ padding: "1rem" }}>Loading…</p>
          ) : showBtech ? (
            <>
              <CreateEducation10thDetails
                variant="application"
                applicationDraft={btechDraft}
                onApplicationDraftChange={patchBtech}
                applicationFieldErrors={btechFieldErrors}
              />
              <CreateEducation12thDetails
                variant="application"
                applicationDraft={btechDraft}
                onApplicationDraftChange={patchBtech}
                applicationFieldErrors={btechFieldErrors}
              />
              {/* <CreateOlympiad
                variant="application"
                applicationDraft={btechDraft}
                onApplicationDraftChange={patchBtech}
                applicationFieldErrors={btechFieldErrors}
              /> */}
            </>
          ) : (
            <CreateGraduationdetails ref={graduationRef} />
          )}

          <div className="form-actions gap-2">
            <button
              className="save-btn"
              type="button"
              onClick={handleSaveAndNext}
              disabled={isSaving}
              style={{ background: "#28a745" }}
            >
              {isSaving ? "Saving..." : "Save & Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
